import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Clock,
  Lightbulb,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { format } from 'date-fns';
import knowledgeGapsService, { type KGRunResponse } from '@/lib/api/knowledgeGaps';
import { DataSurface } from '@/components/platform/DataSurface';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

const PAGE_SIZE = 15;

const STATUS_META: Record<
  KGRunResponse['status'],
  {
    label: string;
    variant: 'default' | 'secondary' | 'destructive' | 'outline';
    dot: string;
  }
> = {
  completed: {
    label: 'Done',
    variant: 'default',
    dot: 'bg-emerald-500',
  },
  running: {
    label: 'Running',
    variant: 'secondary',
    dot: 'bg-amber-500',
  },
  failed: {
    label: 'Failed',
    variant: 'destructive',
    dot: 'bg-red-500',
  },
  pending: {
    label: 'Queued',
    variant: 'outline',
    dot: 'bg-muted-foreground',
  },
};

function pluralize(count: number, singular: string, plural = `${singular}s`) {
  return `${count} ${count === 1 ? singular : plural}`;
}

function formatDate(iso: string | null, includeYear = false): string {
  if (!iso) return 'Unknown time';
  try {
    return format(new Date(iso), includeYear ? 'MMM d, yyyy h:mm a' : 'MMM d, h:mm a');
  } catch {
    return iso;
  }
}

function formatDuration(startIso: string | null, endIso: string | null): string {
  if (!startIso || !endIso) return '—';

  const durationMs = new Date(endIso).getTime() - new Date(startIso).getTime();
  if (!Number.isFinite(durationMs) || durationMs < 0) return '—';

  const minutes = Math.round(durationMs / 60000);
  if (minutes < 1) return 'Under a minute';
  if (minutes < 60) return `${minutes} min`;

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
}

function formatTrigger(triggerType: string): string {
  if (triggerType === 'on_demand') return 'Started manually';
  if (triggerType === 'scheduled') return 'Scheduled search';
  return triggerType.replace(/_/g, ' ');
}

function getRunOutcome(run: KGRunResponse): string {
  if (run.status === 'failed') return 'Search failed';
  if (run.status === 'running') return 'Searching conversations';
  if (run.status === 'pending') return 'Queued to search';

  if (run.summaries_created > 0) {
    return `${pluralize(run.summaries_created, 'gap topic')} found`;
  }

  if (run.findings_created > 0) {
    return 'Open Gaps refreshed';
  }

  if (run.traces_analyzed > 0 || run.traces_fetched > 0) {
    return 'No new gaps found';
  }

  return 'Open Gaps is up to date';
}

function getRunOutcomeDescription(run: KGRunResponse): string {
  if (run.status === 'failed') {
    return 'This search did not finish. Run Find Gaps again. If it keeps failing, contact support.';
  }

  if (run.status === 'running') {
    return 'Hobbes is analyzing conversations and will add recurring issues to Open Gaps.';
  }

  if (run.status === 'pending') {
    return 'This search is waiting to start.';
  }

  if (run.summaries_created > 0) {
    return 'Hobbes reviewed conversations and added recurring missing answers to Open Gaps.';
  }

  if (run.findings_created > 0) {
    return 'Hobbes reviewed recent findings and refreshed Open Gaps.';
  }

  if (run.traces_analyzed > 0 || run.traces_fetched > 0) {
    return 'Hobbes reviewed conversations and did not find new recurring misses.';
  }

  return 'Hobbes checked for new conversation data and Open Gaps is up to date.';
}

function getRunConversationDetail(run: KGRunResponse): string {
  const checkedCount = run.traces_analyzed > 0 ? run.traces_analyzed : run.traces_fetched;
  if (checkedCount > 0) {
    return `${pluralize(checkedCount, 'conversation')} checked`;
  }
  if (run.status === 'completed') {
    return 'Open Gaps checked';
  }
  return 'No conversations checked';
}

function RunDetail({ run }: { run: KGRunResponse }) {
  const statusMeta = STATUS_META[run.status] ?? STATUS_META.pending;
  const outcome = getRunOutcome(run);

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="border-b px-5 py-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-lg font-semibold text-foreground">Gap search</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {formatDate(run.started_at, true)}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <span className={cn('h-2.5 w-2.5 rounded-full', statusMeta.dot)} />
            <Badge variant={statusMeta.variant}>{statusMeta.label}</Badge>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto px-5 py-5">
        <section className="border-b pb-5">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Outcome
          </p>
          <p className="mt-2 text-xl font-semibold text-foreground">{outcome}</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {getRunOutcomeDescription(run)}
          </p>
        </section>

        <section className="grid gap-0 border-b py-5 text-sm sm:grid-cols-2">
          <div className="border-b py-3 sm:border-b-0 sm:border-r sm:pr-5">
            <p className="text-muted-foreground">Review scope</p>
            <p className="mt-1 font-medium text-foreground">{getRunConversationDetail(run)}</p>
          </div>
          <div className="py-3 sm:pl-5">
            <p className="text-muted-foreground">Open gap topics found</p>
            <p className="mt-1 font-medium text-foreground">{run.summaries_created}</p>
          </div>
        </section>

        <section className="space-y-3 py-5 text-sm">
          <div className="flex items-center justify-between gap-4">
            <span className="text-muted-foreground">Started by</span>
            <span className="text-right text-foreground">{formatTrigger(run.trigger_type)}</span>
          </div>
          <div className="flex items-center justify-between gap-4">
            <span className="text-muted-foreground">Duration</span>
            <span className="text-right text-foreground">
              {formatDuration(run.started_at, run.completed_at)}
            </span>
          </div>
          <div className="flex items-center justify-between gap-4">
            <span className="text-muted-foreground">Completed</span>
            <span className="text-right text-foreground">
              {run.completed_at ? formatDate(run.completed_at, true) : 'Not completed yet'}
            </span>
          </div>
        </section>

        <details className="mt-5 border-t pt-4 text-sm">
          <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
            Technical details
          </summary>
          <dl className="mt-3 grid grid-cols-[140px_minmax(0,1fr)] gap-y-2">
            <dt className="text-muted-foreground">Search ID</dt>
            <dd className="truncate font-mono text-xs text-foreground">{run.id}</dd>
            <dt className="text-muted-foreground">Raw findings</dt>
            <dd>{run.findings_created}</dd>
            <dt className="text-muted-foreground">Fetched</dt>
            <dd>{run.traces_fetched}</dd>
          </dl>
        </details>
      </div>
    </div>
  );
}

interface RunHistoryTabProps {
  orgId: string;
}

export function RunHistoryTab({ orgId }: RunHistoryTabProps) {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['knowledge-gaps-runs', orgId, page],
    queryFn: async () => {
      const res = await knowledgeGapsService.getRuns(orgId, {
        page,
        limit: PAGE_SIZE,
      });
      if (!res.success || !res.data) {
        throw new Error(res.error || 'Failed to load gap search history');
      }
      return res.data;
    },
    enabled: !!orgId,
    refetchInterval: (query) => {
      const runs = query.state.data?.runs;
      if (runs?.some((r) => r.status === 'pending' || r.status === 'running')) {
        return 5000;
      }
      return false;
    },
  });

  const runs = data?.runs ?? [];
  const totalRuns = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalRuns / PAGE_SIZE));
  const selectedRun = runs.find((r) => r.id === selectedRunId) ?? null;

  useEffect(() => {
    if (runs.length > 0 && !selectedRunId) {
      setSelectedRunId(runs[0].id);
    }
  }, [runs, selectedRunId]);

  if (isError && !isLoading) {
    return (
      <DataSurface>
        <div className="flex flex-col items-center justify-center gap-3 py-16">
          <AlertCircle className="h-8 w-8 text-destructive" />
          <p className="text-sm text-muted-foreground">
            Failed to load gap search history.
          </p>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      </DataSurface>
    );
  }

  if (isLoading) {
    return (
      <DataSurface className="h-[calc(100vh-13rem)]">
        <div className="grid h-full lg:grid-cols-[360px_minmax(0,1fr)]">
          <div className="border-r p-3">
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-16 w-full rounded-md" />
              ))}
            </div>
          </div>
          <div className="flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </div>
      </DataSurface>
    );
  }

  if (runs.length === 0) {
    return (
      <EmptyState
        icon={Lightbulb}
        title="No gap searches yet"
        description="Go to Find Gaps to start your first analysis."
      />
    );
  }

  return (
    <DataSurface className="h-[calc(100vh-13rem)]">
      <div className="grid h-full lg:grid-cols-[360px_minmax(0,1fr)]">
        <div className="flex min-h-0 flex-col border-b lg:border-b-0 lg:border-r">
          <div className="flex min-h-[52px] items-center gap-3 border-b px-4 py-3">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground">Gap searches</p>
              <p className="text-xs text-muted-foreground">
                {pluralize(totalRuns, 'search', 'searches')}
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="ml-auto h-7 w-7 p-0"
              disabled={isFetching}
              aria-label="Refresh gap search history"
              onClick={() => {
                queryClient.invalidateQueries({
                  queryKey: ['knowledge-gaps-runs', orgId],
                });
              }}
            >
              <RefreshCw className={cn('h-3.5 w-3.5', isFetching && 'animate-spin')} />
            </Button>
          </div>

          <div className="min-h-0 flex-1 overflow-auto divide-y">
            {runs.map((run: KGRunResponse) => {
              const isSelected = selectedRunId === run.id;
              const statusMeta = STATUS_META[run.status] ?? STATUS_META.pending;

              return (
                <button
                  key={run.id}
                  type="button"
                  className={cn(
                    'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/40',
                    isSelected && 'bg-muted/50',
                  )}
                  onClick={() => setSelectedRunId(run.id)}
                >
                  <span className={cn('mt-1 h-2.5 w-2.5 shrink-0 rounded-full', statusMeta.dot)} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-foreground">
                      {getRunOutcome(run)}
                    </span>
                    <span className="mt-1 block text-xs text-muted-foreground">
                      {formatDate(run.started_at)}
                    </span>
                    <span className="mt-1 block text-xs text-muted-foreground">
                      {getRunConversationDetail(run)}
                    </span>
                  </span>
                  <Badge variant={statusMeta.variant} className="shrink-0">
                    {statusMeta.label}
                  </Badge>
                </button>
              );
            })}
          </div>

          {totalPages > 1 ? (
            <div className="flex items-center justify-between border-t px-4 py-2">
              <span className="text-xs text-muted-foreground">
                Page {page} of {totalPages}
              </span>
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => {
                    setPage((p) => Math.max(1, p - 1));
                    setSelectedRunId(null);
                  }}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => {
                    setPage((p) => Math.min(totalPages, p + 1));
                    setSelectedRunId(null);
                  }}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ) : null}
        </div>

        <div className="min-h-0 overflow-hidden">
          {selectedRun ? (
            <RunDetail run={selectedRun} />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              Select a gap search to view details.
            </div>
          )}
        </div>
      </div>
    </DataSurface>
  );
}
